'use client';

import { useEffect, useRef, useState } from 'react';

import { useFetchPostById } from '@ps/features/posts/hooks/useFetchPostById';
import { bioUrl } from '@ps/functions/bio/bioUrls';
import type {
  CommunityChannelMessageLink,
  CommunityChannelMessagePostLink,
} from '@ps/models/lib/community-channel';
import { cn } from '@ps/ui/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { PostChatCard } from '../../../../posts/post-card/PostChatCard';
import { useCommunityContext } from '../../../contexts/community/useCommunityContext';
import { postCardDataFromMessageLink } from '../../../functions/links/postCardDataFromMessageLink';

interface Props {
  links: CommunityChannelMessageLink[];
  isOwn?: boolean;
}

/**
 * Read-only horizontal strip of link cards rendered under a message
 * body. Today only `post` link variants render (other PopshopRN link
 * types fall through to `null`); each is a `PostChatCard` (the shared
 * `PostChatMessageView` wired to open the post-detail modal on click).
 *
 * Chevron-nudge buttons mirror the attachment strip when the row
 * overflows.
 */
export function ChannelMessageLinks({ links, isOwn }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const maxScroll = el.scrollWidth - el.clientWidth;
      setCanScrollLeft(el.scrollLeft > 4);
      setCanScrollRight(el.scrollLeft < maxScroll - 4);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [links.length]);

  const postLinks = links.filter(
    (link): link is CommunityChannelMessagePostLink => link.type === 'post',
  );
  if (postLinks.length === 0) return null;

  const nudge = (direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * 320, behavior: 'smooth' });
  };

  return (
    <div className="relative">
      <div
        ref={scrollRef}
        className="flex gap-2.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {postLinks.map(link => (
          <div key={link.id} className="shrink-0">
            <PostLinkCard link={link} isOwn={isOwn} />
          </div>
        ))}
      </div>
      {canScrollLeft ? (
        <ScrollNudgeButton direction="left" onClick={() => nudge(-1)} />
      ) : null}
      {canScrollRight ? (
        <ScrollNudgeButton direction="right" onClick={() => nudge(1)} />
      ) : null}
    </div>
  );
}

function PostLinkCard({
  link,
  isOwn,
}: {
  link: CommunityChannelMessagePostLink;
  isOwn?: boolean;
}) {
  // Post media is paywall/subscription-gated, so the server redacts
  // URL fields from the `content.links[].medias` snapshot on
  // persistence — only `metadata.blurhash` + `dimensions` survive.
  // The post API resolves per-viewer access at fetch time and
  // returns URLs only if the current user has permission.
  //
  // So always fetch the post by id (matches upstream PopshopRN). The
  // initial paint uses the blurhash as a placeholder cover via
  // `PostChatMessageView`'s `extractCoverSources` fallback; once the
  // fetch lands, the real URLs replace the placeholder. For viewers
  // without access, the post API returns no URLs and the blurhash
  // placeholder stays — that's the intended "locked preview" UX.
  //
  // The canonical link snapshot carries no store id (matches upstream); the
  // post's store is the community creator, so refetch against `creatorId`.
  const { creatorId, isOwner, bioContext } = useCommunityContext();
  const { post, loading, error } = useFetchPostById(
    creatorId ?? undefined,
    link.id,
  );
  // A deleted post is dropped from the search index (no "deleted" post status),
  // so the fetch settles with no row. Hide the card once we KNOW it's gone —
  // only after the fetch resolves (not while loading, so the snapshot still
  // previews) and not on a transient error (keep the snapshot then).
  if (!loading && !error && !post) return null;
  // Default model carries the gate snapshot + resolved `restricted`, so a gated
  // card paints its locked layout at its final height before the fetch lands —
  // no post-fetch growth that would drift the recycling list.
  const cardData = post ?? postCardDataFromMessageLink(link, { isOwner });
  // `permalink` is `<urlCode>/post/<year>/<month>/<slug>`; the modal needs the
  // bare url code. On a successful unlock, checkout returns to the post's own
  // page — the community `bioContext` is always well established, so build it
  // straight through the bio URL builder (non-intercepting: `redirect_to` is a
  // hard nav consumed on checkout return).
  const urlCode = link.permalink?.split('/')[0] ?? '';
  const permalinkPath = link.permalink?.split('/').slice(1).join('/');
  const redirectTo = permalinkPath
    ? bioUrl(bioContext).postPermalink(permalinkPath, 'non-intercepting')
    : undefined;
  return (
    <PostChatCard
      post={cardData}
      storeId={creatorId ?? ''}
      postId={link.id}
      urlCode={urlCode}
      bioContext={bioContext}
      redirectTo={redirectTo}
      isOwn={isOwn}
    />
  );
}

function ScrollNudgeButton({
  direction,
  onClick,
}: {
  direction: 'left' | 'right';
  onClick: () => void;
}) {
  const Icon = direction === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      aria-label={direction === 'left' ? 'Scroll left' : 'Scroll right'}
      onClick={onClick}
      className={cn(
        'absolute top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white text-black shadow-md transition hover:scale-105',
        direction === 'left' ? 'left-1' : 'right-1',
      )}
    >
      <Icon className="h-4 w-4" aria-hidden />
    </button>
  );
}
